/**
 * Component  for event card component
 *
 * @module views/components/EventCard
 * @memberof -Common
 */
import './EventCard.scss';

import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';

import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';

import * as utils from './EventCard';

/**
 * Represents a EventCard component
 * @method
 * @param {EventCardProps} props - React properties passed from composition
 * @returns EventCard
 */

export const EventCard = ( props ) => {
  const { image, eyebrow, headline, eventId, onIntersectionEvent, root, rootMargin, schedule, location, cardAction } = props;
  const eventCardRef = useRef();
  const { intersectionRatio, hasIntersected } = useIntersectionObserver( eventCardRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: [0, 0.5, 1.0]
  } );
  useEffect( () => {
    utils.onIntersection( { eventId, intersectionRatio }, { onIntersectionEvent } );
  }, [hasIntersected, intersectionRatio, onIntersectionEvent] );

  if( !image && !eyebrow && !headline && !schedule && !location ){
    return null;
  }

  const imgHeight = 370 * ( global.devicePixelRatio || 1 );
  const imgWidth = imgHeight * 1.77;

  return (
    <Link_Huge action={ cardAction }
      aria-label='event'
      role='group'
    >
      <div
        className='EventCard EventCard__eventContent'
        ref={ eventCardRef }
        data-visible={ intersectionRatio === 1 }
        data-item-id={ props.eventId }
      >
        <div
          className='EventCard__image'
        >
          { image &&
            <Image
              alt={ image.altText }
              src={ image.imageUrl }
              metaData={ {
                width: imgWidth,
                height: imgHeight
              } }
              shouldUseIntersectionObserver={ false }
            />
          }
        </div>
        <>
          <div className='EventCard__eventDetails'>
            { eyebrow && (
              <div className='EventCard__eyebrow'>
                <Text htmlTag='p'
                  textStyle='eyebrow'
                >
                  { eyebrow }
                </Text>
              </div>
            ) }
            { headline &&
              <div className='EventCard__headline'>
                <Text
                  htmlTag='h3'
                  textStyle='title-5'
                >
                  { headline }
                </Text>
              </div>
            }
            { location &&
              <div className='EventCard__location'>
                <Text
                  htmlTag='p'
                  textStyle='body-2'
                >
                  { location }
                </Text>
              </div>
            }

            { schedule &&
              <div className='EventCard__schedule'>
                <Text
                  htmlTag='p'
                  textStyle='body-2'
                >
                  { schedule }
                </Text>
              </div>
            }
          </div>
        </>
      </div>
    </Link_Huge>
  );
};

/**
 * Method to handle on onIntersection event
 * @param {object} data
 * @param {function} onIntersectionEvent
 */
export const onIntersection = ( data, methods ) => {
  const { eventId, intersectionRatio } = data || {};
  const { onIntersectionEvent } = methods || {};
  // Check if intersectionRatio is not 1, and return early if true
  if( intersectionRatio !== 1 ){
    return;
  }
  // Proceed if the conditions are met
  if( eventId && onIntersectionEvent ){
    onIntersectionEvent( { eventId } );
  }
};


/**
 * Property type definitions
 * @typedef EventCardProps
 * @type {object}
 * @property {string} headline - This is the headline for the Event Card
 * @property {string} description - This is the description for the Event Card
 * @property {string} eyebrow - This is the eyebrow for the Event Card
 * @property {array} images - This is the array of images for the Event Card
 * @property {array} schedule - This is the schedule for the Event Card
 * @property {array} location - This is the location for the Event Card
*/

export const propTypes = {
  headline: PropTypes.string,
  description: PropTypes.string,
  eyebrow: PropTypes.string,
  image: PropTypes.object,
  schedule: PropTypes.string,
  location: PropTypes.string,
  cardAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  eventData: PropTypes.object
};

EventCard.propTypes = propTypes;

export default EventCard;